import React from 'react';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  // Log version to help debug production issues
  React.useEffect(() => {
    console.log('Pagination Component Version: 2.2.0 (mobile-responsive)');
  }, []);
  
  // Generate smart page numbers with ellipsis for large page counts
  const getPageNumbers = () => {
    const pages = [];
    // Reduce visible pages on mobile
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
    const maxVisible = isMobile ? 3 : 9;
    const sidePages = isMobile ? 1 : 3;
    
    if (totalPages <= maxVisible + 2) {
      // If total pages fit within max visible, show all
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      // Calculate range around current page
      let rangeStart = Math.max(2, currentPage - sidePages);
      let rangeEnd = Math.min(totalPages - 1, currentPage + sidePages);
      
      // Ensure we always show at least maxVisible pages
      const rangeSize = rangeEnd - rangeStart + 1;
      if (rangeSize < maxVisible) {
        if (rangeStart === 2) {
          // Near the start, extend to the right
          rangeEnd = Math.min(totalPages - 1, rangeStart + maxVisible - 1);
        } else if (rangeEnd === totalPages - 1) {
          // Near the end, extend to the left
          rangeStart = Math.max(2, rangeEnd - maxVisible + 1);
        }
      }
      
      // Add ellipsis after first page if needed
      if (rangeStart > 2) {
        pages.push('ellipsis-start');
      }
      
      // Add pages in range
      for (let i = rangeStart; i <= rangeEnd; i++) {
        pages.push(i);
      }
      
      // Add ellipsis before last page if needed
      if (rangeEnd < totalPages - 1) {
        pages.push('ellipsis-end');
      }
      
      // Always show last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-2 mt-4">
      {/* First page button - hidden on mobile */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(1)}
        disabled={currentPage === 1}
        className="hidden sm:flex items-center gap-1 h-8 px-2"
        title="First page"
      >
        <ChevronsLeft className="w-4 h-4" />
      </Button>
      
      {/* Previous button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="flex items-center gap-1 h-8 px-2 sm:px-3"
      >
        <ChevronLeft className="w-4 h-4" />
        <span className="hidden sm:inline">Previous</span>
      </Button>
      
      {/* Page numbers - simplified on mobile */}
      <div className="flex gap-1">
        {pageNumbers.map((page, index) => {
          if (typeof page === 'string' && page.startsWith('ellipsis')) {
            return (
              <div
                key={page}
                className="px-1 sm:px-3 py-2 text-xs sm:text-sm text-gray-500 flex items-center"
              >
                ...
              </div>
            );
          }
          
          return (
            <Button
              key={page}
              variant={currentPage === page ? "default" : "outline"}
              size="sm"
              onClick={() => onPageChange(page)}
              className={`min-w-[32px] sm:min-w-[40px] h-8 px-2 sm:px-3 text-xs sm:text-sm ${
                currentPage === page 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : ''
              }`}
            >
              {page}
            </Button>
          );
        })}
      </div>
      
      {/* Next button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="flex items-center gap-1 h-8 px-2 sm:px-3"
      >
        <span className="hidden sm:inline">Next</span>
        <ChevronRight className="w-4 h-4" />
      </Button>
      
      {/* Last page button - hidden on mobile */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages}
        className="hidden sm:flex items-center gap-1 h-8 px-2"
        title="Last page"
      >
        <ChevronsRight className="w-4 h-4" />
      </Button>
    </div>
  );
};

export default Pagination;
